import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, Search, User, Clock, ChevronRight, Download,
  Sparkles, MessageSquare, TrendingUp, Plus, Trash2, Gift, Loader2,
  Shield, UserPlus, Pencil, Save, Send, LogIn,
} from "lucide-react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "sonner";
import DOMPurify from "dompurify";


// iter533 — AI offer generator (uses /admin/users/{id}/generate-offer)
function AdminOfferGenerator({ userId, API, isDark }) {
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/${userId}/generate-offer`);
      setOffer(data.offer);
      toast.success("🎯 Оферта генерирана!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при генериране");
    } finally { setLoading(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Копирано"));
  };

  return (
    <div className={`mt-3 rounded-xl p-3 border ${isDark ? "border-purple-700/40 bg-purple-900/10" : "border-purple-200 bg-purple-50/40"}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-semibold ${isDark ? "text-purple-300" : "text-purple-700"}`}>
          🎯 AI-генерирана персонализирана оферта
        </span>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="admin-generate-offer-btn"
          className="px-3 py-1 rounded-md text-[11px] font-semibold text-white
            bg-purple-500 hover:bg-purple-600 disabled:opacity-50 transition"
        >
          {loading ? "Генерирам..." : (offer ? "🔄 Регенерирай" : "✨ Генерирай")}
        </button>
      </div>
      {offer && (
        <div className={`space-y-2 text-[11px] ${isDark ? "text-gray-200" : "text-gray-800"}`}>
          <div>
            <div className="text-[10px] uppercase opacity-60">Subject</div>
            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold">{offer.subject}</div>
              <button onClick={() => copy(offer.subject)} className="text-[10px] opacity-60 hover:opacity-100">📋</button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase opacity-60">Preview</div>
            <div className="italic">{offer.preview_text}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase opacity-60">Body HTML</div>
            <div
              className="prose prose-sm max-w-none p-2 rounded bg-white text-black text-[11px]"
              /* SEC hardening: sanitize AI-generated HTML before rendering to
                 block any XSS payload that could be injected via a crafted
                 prompt/response. Allows standard formatting + safe links. */
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(offer.body_html || "", {
                  USE_PROFILES: { html: true },
                  FORBID_TAGS: ["style", "script", "iframe", "object", "embed", "form"],
                  FORBID_ATTR: ["style", "onerror", "onload", "onclick"],
                }),
              }}
            />
            <button onClick={() => copy(offer.body_html)} className="text-[10px] opacity-60 hover:opacity-100 mt-1">📋 Копирай HTML</button>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold
              ${isDark ? "bg-purple-700/30 text-purple-200" : "bg-purple-200 text-purple-800"}`}>
              CTA: {offer.primary_cta_text} → {offer.primary_cta_url}
            </span>
          </div>
          {offer.rationale && (
            <div className={`text-[10px] italic mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              💡 Rationale: {offer.rationale}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function HealthMetricsSection({ selectedUser, isDark, t }) {
  const metrics = selectedUser.profile.metrics;
  const measurements = selectedUser.profile.measurements;

  return (
    <div>
      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
        <TrendingUp className="w-4 h-4 text-[#0EA5E9]" />
        {t('admin.metrics')}
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "ИТМ", value: metrics.bmi, unit: "kg/m²" },
          { label: "БМР", value: metrics.bmr, unit: "kcal" },
          { label: "ТДЕЕ", value: metrics.tdee, unit: "kcal" },
          { label: "Целеви кал.", value: metrics.macros?.calories, unit: "kcal" },
        ].map((m) => (
          <div key={m.label} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
            <p className="text-xs text-gray-500 mb-1">{m.label}</p>
            <p className={`text-lg font-bold ${isDark ? 'text-white' : ''}`}>{m.value || "-"}</p>
            <p className="text-xs text-gray-400">{m.unit}</p>
          </div>
        ))}
      </div>

      {metrics.bodyFat && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: "Мастна тъкан", value: `${metrics.bodyFat}%`, cat: metrics.bodyFatCategory?.label },
            { label: "Чиста маса", value: `${metrics.leanMass} кг` },
            { label: "Талия/Ханш", value: metrics.waistHipRatio },
          ].map((m) => (
            <div key={m.label} className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
              <p className="text-[10px] text-gray-500">{m.label}</p>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : ''}`}>{m.value || "-"}</p>
              {m.cat && <p className="text-[10px] text-gray-400">{m.cat}</p>}
            </div>
          ))}
        </div>
      )}

      {measurements && (
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: "Шия", value: measurements.neck, unit: "см" },
            { label: "Талия", value: measurements.waist, unit: "см" },
            { label: "Ханш", value: measurements.hip, unit: "см" },
          ].filter(m => m.value).map((m) => (
            <div key={m.label} className="p-2 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
              <p className="text-[10px] text-gray-500">{m.label}</p>
              <p className={`text-sm font-bold ${isDark ? 'text-white' : ''}`}>{m.value} {m.unit}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Лични данни</p>
          <p className={`font-medium ${isDark ? 'text-white' : ''}`}>
            {selectedUser.profile.personal?.fullName || selectedUser.name}
          </p>
          <p className="text-sm text-gray-500">
            {selectedUser.profile.personal?.gender === "male" ? "Мъж" : "Жена"}, {selectedUser.profile.personal?.age} год.
          </p>
          <p className="text-sm text-gray-500">
            {selectedUser.profile.body?.height} см, {selectedUser.profile.body?.weight} кг
          </p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Цел и активност</p>
          <p className={`font-medium capitalize ${isDark ? 'text-white' : ''}`}>
            {selectedUser.profile.lifestyle?.primaryGoal?.replace("_", " ") || "-"}
          </p>
          <p className="text-sm text-gray-500 capitalize">
            {selectedUser.profile.lifestyle?.activityLevel?.replace("_", " ")}
          </p>
          <p className="text-sm text-gray-500">
            {selectedUser.profile.lifestyle?.dietType}
          </p>
        </div>
      </div>

      {(selectedUser.profile.health?.currentConditions || selectedUser.profile.health?.familyHistory || selectedUser.profile.health?.medications) && (
        <div className="mt-3 space-y-2">
          <h5 className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            Здравна история 
          </h5>
          {selectedUser.profile.health?.currentConditions && (
            <div className="p-2.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-[10px] text-red-500 mb-0.5 font-semibold">Състояния</p>
              <p className="text-xs text-red-700 dark:text-red-300">{selectedUser.profile.health.currentConditions}</p>
            </div>
          )}
          {selectedUser.profile.health?.medications && (
            <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-[10px] text-blue-500 mb-0.5 font-semibold">Медикаменти</p>
              <p className="text-xs text-blue-700 dark:text-blue-300">{selectedUser.profile.health.medications}</p>
            </div>
          )}
          {selectedUser.profile.health?.familyHistory && (
            <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <p className="text-[10px] text-amber-500 mb-0.5 font-semibold">Фамилна история</p>
              <p className="text-xs text-amber-700 dark:text-amber-300">{selectedUser.profile.health.familyHistory}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PatientDossierSection({ userId, isDark, API }) {
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setDossier(null);
    setExpanded(null);
    setLoading(true);
    axios.get(`${API}/admin/users/${userId}/dossier`)
      .then(({ data }) => setDossier(data))
      .catch(() => toast.error("Грешка при зареждане на досието"))
      .finally(() => setLoading(false));
  }, [userId, API]);

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#0EA5E9]" /></div>;
  if (!dossier) return null;

  const lab = dossier.lab_values || {};
  const estrogen = dossier.estrogen_metabolites || {};
  const dil = dossier.dil_calculations;
  const analyses = dossier.lab_analyses || [];
  const hasLab = Object.keys(lab).length > 0;
  const hasEstrogen = Object.keys(estrogen).length > 0;

  const SectionBtn = ({ id, icon: Icon, label, count, color }) => (
    <button onClick={() => setExpanded(expanded === id ? null : id)}
      data-testid={`dossier-${id}-btn`}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
        expanded === id
          ? `border-[${color}]/30 bg-[${color}]/5 text-[${color}]`
          : isDark ? "border-gray-700 text-gray-400 hover:border-gray-600" : "border-gray-200 text-gray-500 hover:border-gray-300"
      }`}>
      <Icon className="w-3.5 h-3.5" style={{ color: expanded === id ? color : undefined }} />
      {label}
      {count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700">{count}</span>}
    </button>
  );

  const labLabels = {
    fastingInsulin: { label: "Инсулин на гладно", unit: "µIU/mL" },
    fastingGlucose: { label: "Глюкоза на гладно", unit: "mg/dL" },
    triglycerides: { label: "Триглицериди", unit: "mg/dL" },
    hdl: { label: "HDL", unit: "mg/dL" },
    ldl: { label: "LDL", unit: "mg/dL" },
    systolicBP: { label: "Систолично нал.", unit: "mmHg" },
    diastolicBP: { label: "Диастолично нал.", unit: "mmHg" },
  };

  return (
    <div data-testid="patient-dossier">
      <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
        <Sparkles className="w-4 h-4 text-[#F59E0B]" />
        Пациентско досие
        
      </h4>
      <div className="flex flex-wrap gap-2 mb-3">
        <SectionBtn id="lab" icon={TrendingUp} label="Лаб. стойности" count={Object.keys(lab).length + Object.keys(estrogen).length} color="#0EA5E9" />
        <SectionBtn id="dil" icon={Sparkles} label="DIL / Фенотип" count={dil ? 1 : 0} color="#8B5CF6" />
        <SectionBtn id="analyses" icon={Search} label="AI Анализи" count={analyses.length} color="#10B981" />
      </div>

      {/* Lab Values */}
      {expanded === "lab" && (
        <div className={`p-4 rounded-xl border mb-3 ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-100"}`}
          data-testid="dossier-lab-panel">
          {hasLab ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(lab).map(([k, v]) => {
                const info = labLabels[k] || { label: k, unit: "" };
                return (
                  <div key={k} className={`p-2.5 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-50"}`}>
                    <p className="text-[10px] text-gray-500">{info.label}</p>
                    <p className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{v} <span className="text-[10px] font-normal text-gray-400">{info.unit}</span></p>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-3">Няма въведени лаб. стойности</p>
          )}
          {hasEstrogen && (
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
              <p className="text-[10px] font-semibold text-gray-500 mb-2">Естрогенови метаболити (µg/g Krea)</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(estrogen).map(([k, v]) => (
                  <div key={k} className={`p-2 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-50"}`}>
                    <p className="text-[10px] text-gray-500">{k}</p>
                    <p className={`text-sm font-bold ${isDark ? "text-white" : ""}`}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* DIL / Phenotype Calculations */}
      {expanded === "dil" && (
        <div className={`p-4 rounded-xl border mb-3 ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-100"}`}
          data-testid="dossier-dil-panel">
          {dil ? (
            <div className="space-y-3">
              {/* Input data used for calculations */}
              <div className={`p-2.5 rounded-lg text-[10px] ${isDark ? "bg-gray-700/50" : "bg-blue-50"}`}>
                <p className="font-semibold text-gray-500 mb-1">Входни данни:</p>
                <span className={isDark ? "text-gray-400" : "text-gray-500"}>
                  {dil.input_data ? (
                    <>Възраст: {dil.input_data.age}, Пол: {dil.input_data.sex === 'male' ? 'М' : 'Ж'}, Ръст: {dil.input_data.height}cm, Тегло: {dil.input_data.weight}kg, Активност: {dil.input_data.activity}, Цел: {dil.input_data.goal}</>
                  ) : "—"}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  { label: "Фенотип", value: `${dil.phenotype?.code || "-"} — ${dil.phenotype?.name || ""}`, color: "#8B5CF6" },
                  { label: "DIL", value: dil.dil?.personalized_dil?.toFixed(1) || "-", color: "#0EA5E9" },
                  { label: "BMR", value: `${dil.energy?.bmr?.toFixed(0) || "-"} kcal`, color: "#F59E0B" },
                  { label: "TDEE", value: `${dil.energy?.tdee?.toFixed(0) || "-"} kcal`, color: "#10B981" },
                  { label: "Финални кал.", value: `${dil.energy?.final_calories?.toFixed(0) || "-"} kcal`, color: "#EF4444" },
                  { label: "Дефицит", value: `${dil.energy?.daily_deficit?.toFixed(0) || "-"} kcal/ден`, color: "#6366F1" },
                  { label: "Телесна маса %", value: `${dil.body_composition?.body_fat_pct?.toFixed(1) || "-"}%`, color: "#F59E0B" },
                  { label: "BMI", value: dil.body_composition?.bmi?.toFixed(1) || "-", color: "#0EA5E9" },
                ].map((m) => (
                  <div key={m.label} className={`p-2.5 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-50"}`}>
                    <p className="text-[10px] text-gray-500">{m.label}</p>
                    <p className="text-sm font-bold" style={{ color: m.color }}>{m.value}</p>
                  </div>
                ))}
              </div>
              {dil.macros && (
                <div className={`p-3 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-50"}`}>
                  <p className="text-[10px] font-semibold text-gray-500 mb-2">Макроси (дневни цели)</p>
                  <div className="flex gap-4 text-xs">
                    <span><strong className="text-[#10B981]">{dil.macros.protein?.toFixed(0)}g</strong> протеин</span>
                    <span><strong className="text-[#F59E0B]">{dil.macros.carbs?.toFixed(0)}g</strong> въгл.</span>
                    <span><strong className="text-[#8B5CF6]">{dil.macros.fat?.toFixed(0)}g</strong> мазнини</span>
                    <span><strong className="text-[#0EA5E9]">{dil.macros.fiber?.toFixed(0)}g</strong> фибри</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-3">Непълен профил — няма DIL изчисления</p>
          )}
        </div>
      )}

      {/* AI Lab Analyses */}
      {expanded === "analyses" && (
        <div className={`p-4 rounded-xl border mb-3 ${isDark ? "bg-gray-800/50 border-gray-700" : "bg-white border-gray-100"}`}
          data-testid="dossier-analyses-panel">
          {analyses.length > 0 ? (
            <div className="space-y-2">
              {analyses.map((a, i) => (
                <div key={a.created_at || `analysis-${i}`} className={`p-3 rounded-lg border ${isDark ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-100"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : ""}`}>
                      {a.analysis?.title || a.filename || `Анализ ${i + 1}`}
                    </p>
                    <span className="text-[10px] text-gray-400">
                      {a.created_at ? format(new Date(a.created_at), "dd MMM yyyy, HH:mm") : ""}
                    </span>
                  </div>
                  {a.analysis?.summary && (
                    <p className={`text-xs mb-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {a.analysis.summary.slice(0, 200)}{a.analysis.summary.length > 200 ? "..." : ""}
                    </p>
                  )}
                  {a.analysis?.findings && a.analysis.findings.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {a.analysis.findings.slice(0, 6).map((f, j) => (
                        <Badge key={`finding-${f.marker}-${j}`} variant="outline" className={`text-[10px] ${
                          f.status === "high" || f.status === "critical" ? "text-red-600 border-red-200" :
                          f.status === "low" ? "text-blue-600 border-blue-200" :
                          "text-green-600 border-green-200"
                        }`}>
                          {f.marker}: {f.value}
                        </Badge>
                      ))}
                    </div>
                  )}
                  {a.analysis?.suspected_conditions && a.analysis.suspected_conditions.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {a.analysis.suspected_conditions.map((c, j) => (
                        <Badge key={`cond-${c.condition}-${j}`} className={`text-[10px] ${
                          c.risk_level === "high" ? "bg-red-100 text-red-700" :
                          c.risk_level === "moderate" ? "bg-amber-100 text-amber-700" :
                          "bg-green-100 text-green-700"
                        }`}>
                          {c.condition}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-3">Няма качени AI анализи</p>
          )}
        </div>
      )}
    </div>
  );
}

function UserDetails({
  selectedUser, isDark, t, handleDeleteUser, handleAddCredits,
  creditAmount, setCreditAmount, giftPlan, setGiftPlan,
  giftMonths, setGiftMonths, giftLoading, handleGiftSubscription,
  handleRevokeSubscription,
  userQueries, API, onUserUpdated, financialAudit
}) {
  const [editingRole, setEditingRole] = useState(false);
  const [newRole, setNewRole] = useState(selectedUser.role || "user");
  const [editingProfile, setEditingProfile] = useState(false);
  const [editName, setEditName] = useState(selectedUser.name || "");
  const [editEmail, setEditEmail] = useState(selectedUser.email || "");
  const [saving, setSaving] = useState(false);
  const [clinicians, setClinicians] = useState([]);
  const [assignClinician, setAssignClinician] = useState(selectedUser.clinician_id || "");
  const [assigning, setAssigning] = useState(false);
  // iter595 — dynamic plan catalog (single source of truth from /api/pricing/catalog)
  const [planCatalog, setPlanCatalog] = useState(null);

  useEffect(() => {
    // Fetch approved clinicians for assignment dropdown
    axios.get(`${API}/admin/clinician-applications`).then(({ data }) => {
      const approved = (data.applications || []).filter(a => a.clinician_status === "approved");
      setClinicians(approved);
    }).catch(() => {});
    // iter595 — load BG-market catalog so the gift dropdown shows the actual prices
    axios.get(`${API}/pricing/catalog?market=bg`).then(({ data }) => {
      setPlanCatalog(data);
    }).catch(() => {});
  }, [API]);

  // iter595 — derive renewal info from subscription doc for the "Plan details" card
  const sub = selectedUser.subscription || {};
  const planId = sub.plan || (sub.status === "active" ? "core" : "free");
  const planInfo = planCatalog?.plans?.find(p => p.id === planId);
  const founderActive = planCatalog?.founder?.active && planId === "pro" && sub.founder;
  const renewalDate = sub.end_date || sub.trial_end || sub.renewal_at;
  const daysUntilRenewal = renewalDate ? Math.ceil((new Date(renewalDate) - new Date()) / 86400000) : null;
  const startedAt = sub.start_date || sub.gifted_at || sub.activated_at || sub.started_at;
  const lastPayment = financialAudit?.total_revenue_eur;

  const handleAssignClinician = async () => {
    setAssigning(true);
    try {
      if (assignClinician) {
        await axios.post(`${API}/admin/assign-patient`, { patient_id: selectedUser.id, clinician_id: assignClinician });
        toast.success("Пациентът е насочен към лекар");
      } else {
        await axios.delete(`${API}/admin/unassign-patient/${selectedUser.id}`);
        toast.success("Пациентът е отвързан от лекар");
      }
      if (onUserUpdated) onUserUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка");
    } finally { setAssigning(false); }
  };

  const ROLES = [
    { value: "user", label: "Потребител", color: "bg-gray-100 text-gray-600" },
    { value: "admin", label: "Админ", color: "bg-red-100 text-red-700" },
    { value: "author", label: "Автор", color: "bg-purple-100 text-purple-700" },
    { value: "clinician", label: "Лекар", color: "bg-blue-100 text-blue-700" },
  ];

  const handleChangeRole = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/role`, { role: newRole });
      toast.success(`Ролята е променена на ${ROLES.find(r => r.value === newRole)?.label}`);
      setEditingRole(false);
      if (onUserUpdated) onUserUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка");
    } finally { setSaving(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/profile`, {
        name: editName, email: editEmail,
      });
      toast.success("Профилът е обновен");
      setEditingProfile(false);
      if (onUserUpdated) onUserUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка");
    } finally { setSaving(false); }
  };

  const currentRole = ROLES.find(r => r.value === (selectedUser.role || "user"));

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center">
          <span className="text-white text-2xl font-bold">
            {selectedUser.name?.charAt(0)?.toUpperCase()}
          </span>
        </div>
        <div className="flex-1">
          {editingProfile ? (
            <div className="space-y-2">
              <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Име" className="h-8 text-sm" data-testid="edit-name-input" />
              <Input value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="Email" className="h-8 text-sm" data-testid="edit-email-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSaveProfile} disabled={saving} className="h-7 text-xs bg-[#10B981] hover:bg-[#059669] text-white" data-testid="save-profile-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Запази
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingProfile(false)} className="h-7 text-xs">Отказ</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : ''}`}>{selectedUser.name}</h3>
                <button onClick={() => { setEditName(selectedUser.name || ""); setEditEmail(selectedUser.email || ""); setEditingProfile(true); }}
                  className="text-gray-400 hover:text-gray-600" data-testid="edit-profile-btn">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="text-gray-500">{selectedUser.email}</p>
            </>
          )}
          <div className="flex gap-2 mt-2 flex-wrap">
            {/* Role badge with change option */}
            {editingRole ? (
              <div className="flex items-center gap-1.5" data-testid="role-editor">
                <select value={newRole} onChange={e => setNewRole(e.target.value)}
                  className={`h-7 text-xs rounded-md border px-2 ${isDark ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`}
                  data-testid="role-select">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                <Button size="sm" onClick={handleChangeRole} disabled={saving || newRole === selectedUser.role}
                  className="h-7 text-[10px] px-2 bg-[#6366F1] hover:bg-[#5558E6] text-white" data-testid="save-role-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3 mr-0.5" />} Запази
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingRole(false)} className="h-7 text-[10px] px-2">X</Button>
              </div>
            ) : (
              <Badge className={`${currentRole?.color} cursor-pointer`} onClick={() => { setNewRole(selectedUser.role || "user"); setEditingRole(true); }}
                data-testid="role-badge">
                <Shield className="w-3 h-3 mr-1" /> {currentRole?.label || selectedUser.role}
              </Badge>
            )}
            <Badge variant="outline">{selectedUser.credits} credits</Badge>
            {selectedUser.subscription?.status && (
              <Badge className={
                selectedUser.subscription.status === "active" ? "bg-green-100 text-green-700" :
                selectedUser.subscription.status === "trialing" ? "bg-blue-100 text-blue-700" :
                selectedUser.subscription.status === "expired" || selectedUser.subscription.status === "trial_expired" ? "bg-red-100 text-red-600" :
                "bg-gray-100 text-gray-500"
              }>
                {selectedUser.subscription.status === "active" ? `${(selectedUser.subscription.plan || "core").toUpperCase()} Active` :
                 selectedUser.subscription.status === "trialing" ? "Trial" :
                 selectedUser.subscription.status === "expired" || selectedUser.subscription.status === "trial_expired" ? "Expired" :
                 selectedUser.subscription.status}
                {selectedUser.subscription.gifted && " (Gift)"}
              </Badge>
            )}
            {selectedUser.subscription?.trial_end && (
              <Badge variant="outline" className="text-xs">
                <Clock className="w-3 h-3 mr-1" />
                Trial: {format(new Date(selectedUser.subscription.trial_end), "MMM d, yyyy")}
              </Badge>
            )}
            {selectedUser.created_at && (
              <Badge variant="outline">
                <Clock className="w-3 h-3 mr-1" />
                {format(new Date(selectedUser.created_at), "MMM d, yyyy")}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-3" data-testid="credit-manager">
            <Input type="number" placeholder="+50 / -10"
              value={creditAmount} onChange={e => setCreditAmount(e.target.value)}
              className="w-24 h-8 text-sm" data-testid="credit-input" />
            <Button size="sm" variant="outline" onClick={handleAddCredits}
              disabled={!creditAmount} className="h-8 text-xs" data-testid="add-credits-btn">
              <Plus className="w-3 h-3 mr-1" /> Кредити
            </Button>
          </div>
          <div className={`flex items-center gap-2 mt-2 p-2 rounded-lg ${isDark ? 'bg-gray-600/30' : 'bg-blue-50'}`} data-testid="gift-subscription">
            <select value={giftPlan} onChange={e => setGiftPlan(e.target.value)}
              className={`h-8 text-xs rounded-md border px-2 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
              data-testid="gift-plan-select">
              {(planCatalog?.plans || []).filter(p => ["core", "pro", "clinic"].includes(p.id)).map(p => (
                <option key={p.id} value={p.id}>
                  {p.name_bg || p.name_en || p.id.toUpperCase()} (&#8364;{(p.price ?? 0).toFixed(2)}/мес · {p.credits_included || 0} кр.)
                </option>
              ))}
              {!planCatalog && (<>
                <option value="core">Core (зарежда…)</option>
                <option value="pro">Pro (зарежда…)</option>
              </>)}
            </select>
            <Input type="number" placeholder="мес."
              value={giftMonths} onChange={e => setGiftMonths(e.target.value)}
              className="w-16 h-8 text-xs" min="1" max="12" data-testid="gift-months-input" />
            <Button size="sm" onClick={handleGiftSubscription} disabled={giftLoading}
              className="h-8 text-xs bg-[#10B981] hover:bg-[#059669] text-white" data-testid="gift-subscription-btn">
              {giftLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Gift className="w-3 h-3 mr-1" />}
              Подари
            </Button>
            {selectedUser.subscription?.status === "active" && (
              <Button size="sm" variant="destructive" onClick={handleRevokeSubscription}
                className="h-8 text-xs" data-testid="revoke-subscription-btn">
                <Trash2 className="w-3 h-3 mr-1" /> Премахни
              </Button>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="destructive"
              onClick={() => handleDeleteUser(selectedUser.id, selectedUser.name)}
              className="h-8 text-xs" data-testid="delete-user-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Изтрий потребител
            </Button>
            {/* iter641 — Impersonate user */}
            {selectedUser.role !== "admin" && (
              <Button size="sm" variant="outline"
                onClick={async () => {
                  const ok = window.confirm(
                    `Влез като ${selectedUser.email}?\n\nЩе видиш платформата от тяхна гледна точка. ` +
                    `Действието се записва в audit лога.`
                  );
                  if (!ok) return;
                  try {
                    const adminToken = localStorage.getItem("access_token");
                    const res = await axios.post(
                      `${API}/admin/users/${selectedUser.id}/impersonate`,
                      {},
                      { headers: { Authorization: `Bearer ${adminToken}` } }
                    );
                    const { access_token, target_user } = res.data || {};
                    if (!access_token) throw new Error("Няма получен токен");
                    localStorage.setItem("impersonation_admin_token", adminToken || "");
                    localStorage.setItem("impersonation_target_email", target_user?.email || "");
                    localStorage.setItem("impersonation_active", "true");
                    localStorage.setItem("impersonation_readonly", "false");
                    localStorage.setItem("impersonation_return_path", "/admin");
                    localStorage.setItem("access_token", access_token);
                    window.location.href = "/dashboard";
                  } catch (err) {
                    const msg = err?.response?.data?.detail || err.message || "Грешка";
                    window.alert("Грешка при impersonation: " + msg);
                  }
                }}
                className="h-8 text-xs border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="impersonate-user-btn"
              >
                <LogIn className="w-3 h-3 mr-1" /> Влез като
              </Button>
            )}
          </div>

          {/* iter595 — Plan details card (renewal, credits, founder, last payment) */}
          <div
            className={`mt-3 p-3 rounded-xl border ${isDark ? "bg-gray-800/40 border-gray-700" : "bg-indigo-50/40 border-indigo-100"}`}
            data-testid="plan-details-card"
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[11px] font-semibold uppercase tracking-wide ${isDark ? "text-indigo-300" : "text-indigo-700"}`}>
                💳 Детайли за плана
              </span>
              {founderActive && (
                <Badge className="bg-amber-100 text-amber-700 text-[10px]" data-testid="founder-badge">
                  🏆 Основател (заключена цена)
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-[11px]">
              {/* Plan tier */}
              <div className={`p-2 rounded-md ${isDark ? "bg-gray-700/40" : "bg-white"}`}>
                <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>План</div>
                <div className={`font-bold text-sm ${isDark ? "text-white" : "text-gray-900"}`} data-testid="plan-tier-value">
                  {planInfo?.name_bg || planId.toUpperCase()}
                </div>
                {planInfo && (
                  <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    €{planInfo.price?.toFixed(2)}/мес · {planInfo.credits_included || 0} кр./мес
                  </div>
                )}
              </div>

              {/* Credits balance */}
              <div className={`p-2 rounded-md ${isDark ? "bg-gray-700/40" : "bg-white"}`}>
                <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>Баланс кредити</div>
                <div className={`font-bold text-sm ${isDark ? "text-white" : "text-gray-900"}`} data-testid="credit-balance-value">
                  {selectedUser.credits || 0}
                </div>
                {planInfo?.credits_included > 0 && (
                  <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    от {planInfo.credits_included}/мес
                  </div>
                )}
              </div>

              {/* Renewal/expiry */}
              <div className={`p-2 rounded-md ${isDark ? "bg-gray-700/40" : "bg-white"}`}>
                <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {sub.status === "trialing" ? "Trial изтича" : "Подновяване"}
                </div>
                <div className={`font-bold text-sm ${isDark ? "text-white" : "text-gray-900"}`} data-testid="renewal-date-value">
                  {renewalDate ? format(new Date(renewalDate), "d MMM yyyy") : "—"}
                </div>
                {daysUntilRenewal !== null && (
                  <div className={`text-[10px] ${
                    daysUntilRenewal < 0 ? "text-red-500"
                    : daysUntilRenewal < 7 ? "text-amber-500"
                    : isDark ? "text-gray-400" : "text-gray-500"
                  }`}>
                    {daysUntilRenewal < 0 ? `Изтекъл преди ${Math.abs(daysUntilRenewal)} дни`
                      : daysUntilRenewal === 0 ? "Днес"
                      : `След ${daysUntilRenewal} дни`}
                  </div>
                )}
              </div>

              {/* Started / last payment */}
              <div className={`p-2 rounded-md ${isDark ? "bg-gray-700/40" : "bg-white"}`}>
                <div className={`text-[9px] uppercase ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {sub.gifted ? "Подарен на" : "Започнат"}
                </div>
                <div className={`font-bold text-sm ${isDark ? "text-white" : "text-gray-900"}`} data-testid="plan-started-value">
                  {startedAt ? format(new Date(startedAt), "d MMM yyyy") : "—"}
                </div>
                {lastPayment !== undefined && lastPayment > 0 && (
                  <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Платил досега: €{Number(lastPayment).toFixed(2)}
                  </div>
                )}
              </div>
            </div>

            {/* Status row */}
            <div className={`mt-2 pt-2 border-t flex items-center justify-between gap-2 text-[10px] ${isDark ? "border-gray-700 text-gray-400" : "border-indigo-100 text-gray-500"}`}>
              <span>
                Статус: <span className={`font-semibold ${
                  sub.status === "active" ? "text-emerald-500"
                  : sub.status === "trialing" ? "text-blue-500"
                  : (sub.status === "expired" || sub.status === "trial_expired") ? "text-red-500"
                  : ""
                }`}>{sub.status || "free"}</span>
                {sub.gifted && <span className="ml-1 text-amber-500">· 🎁 Gift</span>}
              </span>
              {planInfo?.price_annual && (
                <span title="Цена за година">
                  Годишно: €{Number(planInfo.price_annual).toFixed(0)}
                </span>
              )}
            </div>
          </div>
          {/* Assign to Clinician */}
          {selectedUser.role === "user" && clinicians.length > 0 && (
            <div className={`mt-2 p-2 rounded-lg ${isDark ? 'bg-gray-600/30' : 'bg-green-50'}`} data-testid="assign-clinician-section">
              <p className="text-[10px] font-semibold text-gray-500 mb-1.5">Насочване към лекар</p>
              <div className="flex items-center gap-2">
                <select value={assignClinician} onChange={e => setAssignClinician(e.target.value)}
                  className={`h-8 text-xs rounded-md border px-2 flex-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                  data-testid="assign-clinician-select">
                  <option value="">— Без лекар —</option>
                  {clinicians.map(c => (
                    <option key={c.id} value={c.id}>{c.name} ({c.specialty})</option>
                  ))}
                </select>
                <Button size="sm" onClick={handleAssignClinician} disabled={assigning}
                  className="h-8 text-xs bg-[#10B981] hover:bg-[#059669] text-white" data-testid="assign-clinician-btn">
                  {assigning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Users className="w-3 h-3 mr-1" />}
                  Насочи
                </Button>
              </div>
            </div>
          )}
          {/* Quick Message to User */}
          <AdminQuickMessage userId={selectedUser.id} userName={selectedUser.name} isDark={isDark} API={API} />
        </div>
      </div>

      {selectedUser.profile?.metrics ? (
        <HealthMetricsSection selectedUser={selectedUser} isDark={isDark} t={t} />
      ) : selectedUser.profile?.completedOnboarding ? (
        <div>
          <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            <TrendingUp className="w-4 h-4 text-[#0EA5E9]" />
            Profile Data
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-xs text-gray-500">Personal</p>
              <p className={`font-medium ${isDark ? 'text-white' : ''}`}>
                {selectedUser.profile.personal?.fullName || selectedUser.name}
              </p>
              <p className="text-sm text-gray-500">
                {selectedUser.profile.personal?.gender}, {selectedUser.profile.personal?.age} years
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <p className="text-xs text-gray-500">Body</p>
              <p className={`font-medium ${isDark ? 'text-white' : ''}`}>
                {selectedUser.profile.body?.height}cm / {selectedUser.profile.body?.weight}kg
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center text-gray-400">
          <User className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t('admin.no_profile')}</p>
        </div>
      )}

      {/* Patient Dossier */}
      <PatientDossierSection userId={selectedUser.id} isDark={isDark} API={API} />

      {/* iter532 — Financial audit / behaviour / recommendations */}
      {financialAudit && (
        <div data-testid="admin-financial-audit" className={`rounded-2xl p-4 border ${
          isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-gradient-to-br from-emerald-50 to-blue-50 border-slate-200"
        }`}>
          <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            💰 Финансов одит + поведение
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {[
              { k: "Кредити", v: financialAudit.credits_balance, color: financialAudit.credits_balance < 3 ? "#EF4444" : "#10B981" },
              { k: "Приход", v: `€${financialAudit.revenue_eur}`, color: "#0EA5E9" },
              { k: "AI разход", v: `€${financialAudit.ai_cost_eur}`, color: "#F59E0B" },
              { k: "Печалба", v: `€${financialAudit.net_profit_eur}`, color: financialAudit.net_profit_eur >= 0 ? "#10B981" : "#EF4444" },
            ].map((m) => (
              <div key={m.k} className={`rounded-xl p-2 text-center ${isDark ? "bg-[hsl(223,47%,14%)]" : "bg-white"}`}>
                <div className="text-[10px] text-gray-500">{m.k}</div>
                <div className="text-base font-bold" style={{ color: m.color }}>{m.v}</div>
              </div>
            ))}
          </div>
          <div className="mb-3">
            <p className={`text-xs font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Изразходвани анализи (по тип)
            </p>
            <div className="grid grid-cols-2 gap-1.5 text-[11px]">
              {Object.entries(financialAudit.consumption || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className={isDark ? "text-gray-400" : "text-gray-600"}>
                    {{
                      lab_analyses: "Лаб. анализи",
                      meal_reviews: "Meal reviews",
                      medical_image_scans: "Скенирания",
                      weekly_reports: "Седмични отчети",
                      ai_queries: "AI заявки",
                    }[k] || k}:
                  </span>
                  <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mb-3">
            <p className={`text-xs font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Поведение (доминантен модул: <b>{financialAudit.dominant_module}</b>)
            </p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(financialAudit.behaviour_profile || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([k, v]) => (
                  <span key={k} className={`px-2 py-0.5 rounded-full text-[10px] ${
                    isDark ? "bg-[hsl(223,47%,18%)] text-gray-300" : "bg-white text-gray-700 border border-gray-200"
                  }`}>
                    {k}: {v}
                  </span>
                ))}
            </div>
          </div>
          <div>
            <p className={`text-xs font-semibold mb-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Препоръчани действия
            </p>
            <ul className="space-y-1.5">
              {(financialAudit.recommendations || []).map((r, i) => (
                <li key={i} className={`text-[11px] flex gap-2 items-start ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${
                    r.priority === "high" ? "bg-red-500/15 text-red-500"
                    : r.priority === "medium" ? "bg-amber-500/15 text-amber-600"
                    : "bg-blue-500/15 text-blue-500"
                  }`}>{r.priority.toUpperCase()}</span>
                  <span>{r.message_bg}</span>
                </li>
              ))}
            </ul>
            {/* iter533 — AI-generated personalised offer */}
            <AdminOfferGenerator userId={selectedUser.id} API={API} isDark={isDark} />
          </div>
        </div>
      )}

      <div>
        <h4 className={`font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
          <MessageSquare className="w-4 h-4 text-[#6366F1]" />
          {t('admin.query_history')}
        </h4>
        <ScrollArea className="h-[200px]">
          {userQueries.length > 0 ? (
            <div className="space-y-2">
              {userQueries.map((q) => (
                <div key={q.id || q.created_at} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <p className={`text-sm ${isDark ? 'text-white' : ''}`}>{q.query}</p>
                  <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {q.created_at ? format(new Date(q.created_at), "MMM d, HH:mm") : ""}
                    <Badge variant="outline" className="text-xs">
                      {q.result?.credits_used || 0} credits
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-400 py-8">No queries yet</p>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

function AdminQuickMessage({ userId, userName, isDark, API }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, { recipient_id: userId, text: text.trim() });
      setText("");
      toast.success(`Съобщение изпратено до ${userName}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при изпращане");
    } finally { setSending(false); }
  };

  return (
    <div className={`mt-2 p-2 rounded-lg ${isDark ? 'bg-[hsl(223,47%,15%)]' : 'bg-indigo-50'}`} data-testid="admin-quick-message">
      <p className="text-[10px] font-semibold text-gray-500 mb-1.5 flex items-center gap-1">
        <MessageSquare className="w-3 h-3" /> Съобщение до потребителя
      </p>
      <div className="flex items-center gap-1.5">
        <Input value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") send(); }}
          placeholder="Напишете..."
          className={`h-7 text-xs flex-1 ${isDark ? 'bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)] text-white' : ''}`}
          data-testid="admin-message-input" />
        <Button size="sm" className="h-7 text-xs bg-[#0EA5E9] hover:bg-[#0284C7] text-white"
          onClick={send} disabled={sending || !text.trim()} data-testid="admin-send-btn">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
        </Button>
      </div>
    </div>
  );
}



export default function AdminUsersPanel({
  isDark, t, users, selectedUser, searchTerm, setSearchTerm,
  handleSelectUser, handleExport, handleDeleteUser, handleAddCredits,
  creditAmount, setCreditAmount, giftPlan, setGiftPlan,
  giftMonths, setGiftMonths, giftLoading, handleGiftSubscription,
  handleRevokeSubscription,
  userQueries, API, onUserUpdated, financialAudit
}) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [creating, setCreating] = useState(false);

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Попълнете всички полета"); return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users/create`, createForm);
      toast.success(`Потребител ${createForm.email} създаден`);
      setShowCreateDialog(false);
      setCreateForm({ name: "", email: "", password: "", role: "user" });
      if (onUserUpdated) onUserUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Грешка при създаване");
    } finally { setCreating(false); }
  };

  return (
    <>
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <Card className={`lg:col-span-2 ${isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}`}>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2 min-w-0">
              <Users className="w-5 h-5 text-[#0EA5E9] flex-shrink-0" />
              <span className="truncate">{t('admin.title')}</span>
            </CardTitle>
            <div className="flex flex-wrap gap-1.5 min-w-0">
              <Button size="sm" onClick={() => setShowCreateDialog(true)} className="bg-[#10B981] hover:bg-[#059669] text-white" data-testid="create-user-btn">
                <UserPlus className="w-4 h-4 mr-1" /> Нов
              </Button>
              <Button size="sm" variant="outline"
                onClick={async () => {
                  if (!window.confirm("Re-enrich старите meal записи?\n\nЩе fuzzy-match-нем USDA данните за всички храни с 0 калории/macros (не-supplements). Действието е safe — само update-ва съществуващи документи.")) return;
                  try {
                    const t = localStorage.getItem("access_token");
                    const dry = await axios.post(`${API}/admin/meals/re-enrich?dry_run=true&limit=5000`, {}, { headers: { Authorization: `Bearer ${t}` } });
                    const c = dry.data?.candidates || 0;
                    if (c === 0) { window.alert("Няма meal записи за re-enrich-ване."); return; }
                    if (!window.confirm(`Намерени ${c} записа за update. Продължи?`)) return;
                    const res = await axios.post(`${API}/admin/meals/re-enrich?dry_run=false&limit=5000`, {}, { headers: { Authorization: `Bearer ${t}` } });
                    window.alert(`Готово ✓ Updated: ${res.data?.updated || 0} meal entries.`);
                  } catch (e) {
                    window.alert("Грешка: " + (e?.response?.data?.detail || e.message));
                  }
                }}
                className="border-amber-500 text-amber-700 hover:bg-amber-50"
                data-testid="admin-re-enrich-btn"
                title="Re-enrich стари meal записи с празни macros"
              >
                <Sparkles className="w-4 h-4 mr-1" /> Re-enrich
              </Button>
              <Button size="sm" variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-1" />
                {t('admin.export')}
              </Button>
            </div>
          </div>
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder={t('admin.search')} value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`pl-10 ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-[#F8FAFC]'}`} />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[500px] [&>div>div]:!block">
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {users.map((u) => (
                <button key={u.id} onClick={() => handleSelectUser(u)}
                  className={`w-full text-left px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedUser?.id === u.id ? 'bg-[#0EA5E9]/5 dark:bg-[#0EA5E9]/10' : ''
                  }`}>
                  <div className="flex items-center justify-between gap-2 min-w-0">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-medium">{u.name?.charAt(0)?.toUpperCase() || "?"}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.name}</p>
                        <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {u.profile?.completedOnboarding && (
                        <Badge className="bg-green-100 text-green-700 text-xs">Profile</Badge>
                      )}
                      {u.subscription?.status && (
                        <Badge className={`text-xs ${
                          u.subscription.status === "active" ? "bg-green-100 text-green-700" :
                          u.subscription.status === "trialing" ? "bg-blue-100 text-blue-700" :
                          u.subscription.status === "expired" || u.subscription.status === "trial_expired" ? "bg-red-100 text-red-600" :
                          "bg-gray-100 text-gray-500"
                        }`}>
                          {u.subscription.status === "active" ? (u.subscription.plan || "Active") :
                           u.subscription.status === "trialing" ? "Trial" :
                           u.subscription.status === "expired" || u.subscription.status === "trial_expired" ? "Expired" :
                           u.subscription.status}
                        </Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className={`lg:col-span-3 ${isDark ? 'bg-gray-800 border-gray-700' : 'card-elevated border-0'}`}
        data-testid="admin-patient-details">
        <CardHeader>
          <CardTitle className="text-lg">{t('admin.patient_details')}</CardTitle>
        </CardHeader>
        <CardContent>
          {selectedUser ? (
            <UserDetails
              selectedUser={selectedUser} isDark={isDark} t={t}
              handleDeleteUser={handleDeleteUser} handleAddCredits={handleAddCredits}
              handleRevokeSubscription={handleRevokeSubscription}
              creditAmount={creditAmount} setCreditAmount={setCreditAmount}
              giftPlan={giftPlan} setGiftPlan={setGiftPlan}
              giftMonths={giftMonths} setGiftMonths={setGiftMonths}
              giftLoading={giftLoading} handleGiftSubscription={handleGiftSubscription}
              userQueries={userQueries} API={API} onUserUpdated={onUserUpdated}
              financialAudit={financialAudit}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <User className="w-16 h-16 mb-4 opacity-30" />
              <p>Select a patient to view details</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>

    {/* Create User Dialog */}
    <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
      <DialogContent className={isDark ? "bg-gray-800 border-gray-700" : ""}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-[#10B981]" /> Създаване на потребител
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2">
          <Input placeholder="Име" value={createForm.name} onChange={e => setCreateForm(f => ({...f, name: e.target.value}))}
            className={isDark ? "bg-gray-700 border-gray-600" : ""} data-testid="create-name" />
          <Input placeholder="Email" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({...f, email: e.target.value}))}
            className={isDark ? "bg-gray-700 border-gray-600" : ""} data-testid="create-email" />
          <Input placeholder="Парола" type="password" value={createForm.password} onChange={e => setCreateForm(f => ({...f, password: e.target.value}))}
            className={isDark ? "bg-gray-700 border-gray-600" : ""} data-testid="create-password" />
          <select value={createForm.role} onChange={e => setCreateForm(f => ({...f, role: e.target.value}))}
            className={`w-full h-10 rounded-md border px-3 text-sm ${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-200"}`}
            data-testid="create-role">
            <option value="user">Потребител</option>
            <option value="admin">Админ</option>
            <option value="author">Автор</option>
            <option value="clinician">Лекар</option>
          </select>
          <Button onClick={handleCreateUser} disabled={creating} className="w-full bg-[#10B981] hover:bg-[#059669] text-white" data-testid="confirm-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Създай потребител
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
